import * as React from "react";
import logo from "../../assets/center_all_pic/AceLOGO.png";
import { useSelector, useDispatch } from "react-redux";
import { Box, Typography, Button, createTheme } from "@mui/material";
import { DashboardLayout } from "@toolpad/core/DashboardLayout";
import { jwtDecode } from "jwt-decode";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
} from "react-router-dom";
import { AppProvider } from "@toolpad/core/AppProvider";
import { fetchUser, LogoutUser } from "../../utils/Apis";
import { clearUserData, selectUser } from "../../features/userSlice";
import { logout } from "../../features/authSlice";
import { getNavigationForRole } from "./dashboardsComponents/Layout/Config/NavigationConfig";
import { useDemoRouter } from "@toolpad/core/internal";
import DashboardContent from "./dashboardsComponents/Layout/DashboardContent";
import StaffContent from "./dashboardsComponents/Layout/TabComponents/StaffContent";
import StudentsTab from "./dashboardsComponents/Layout/Tabs/StudentsTab";
import UploadsTab from "./dashboardsComponents/Layout/Tabs/UploadsTab";
import { demoTheme } from "./Theme";
import CoursesTab from "./dashboardsComponents/Layout/Tabs/CoursesTab";
import RequestsTab from "./dashboardsComponents/Layout/Tabs/RequestsTab";
import BranchesTab from "./dashboardsComponents/Layout/Tabs/BranchesTab";
import { useEffect } from "react";
import { unwrapResult } from "@reduxjs/toolkit";
import Loader from "../commonComponents/Loader";
import { useState } from "react";
import SettingsTab from "./dashboardsComponents/Layout/Tabs/SettingsTab";
import SEO from "../SEO";

function DemoPageContent({ pathname }) {
  return (
    <Box
      sx={{
        py: 4,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        textAlign: "center",
      }}
    >
      <Typography>Dashboard content for {pathname}</Typography>
    </Box>
  );
}
function PageContent({ pathname, role, userId }) {
  const segment = pathname.split("/")[1] || "dashboard";

  switch (segment) {
    // case "staff":
    //   return <StaffContent role={role} userId={userId} />;
    case "students":
      return <StudentsTab role={role} userId={userId} />;
    case "requests":
      return <RequestsTab role={role} userId={userId} />;
    case "branches":
      return <BranchesTab role={role} userId={userId} />;
    // case 'attendance':
    //   return <AttendanceContent role={role} userId={userId} />;
    // case 'self-attendance':
    //   return <AttendanceContent role={role} userId={userId} />;
    case "uploads":
      return <UploadsTab role={role} userId={userId} />;
    case "dashboard":
      return <DashboardContent role={role} userId={userId} />;
    case "courses":
      return <CoursesTab role={role} userId={userId} />;
    case "profile":
      return <SettingsTab />;
    default:
      return <DashboardContent role={role} userId={userId} />;
  }
}

function AdminDashboard() {
  // Theme configuration
  // const demoTheme = createTheme({
  //   cssVariables: {
  //     colorSchemeSelector: "data-toolpad-color-scheme",
  //   },
  //   colorSchemes: { light: true, dark: true },
  //   breakpoints: {
  //     values: {
  //       xs: 0,
  //       sm: 600,
  //       md: 600,
  //       lg: 1200,
  //       xl: 1536,
  //     },
  //   },
  // });

  const dispatch = useDispatch();

  const token = useSelector((state) => state.auth.token);
  const userState = useSelector((state) => state.user.data); // user data selector
  const user = jwtDecode(token);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!userState) {
      dispatch(fetchUser())
        .unwrap()
        .then((fetchedUser) => {})
        .catch((error) => {
          console.error("User fetch failed:", error);
          dispatch(logout());
          dispatch(clearUserData());
        });
      setLoading(false);
    }
    if (userState?.role === "student") {
      if (userState?.studentDetails?.status !== "active") {
        dispatch(logout());
        dispatch(clearUserData());
      }
    }
  }, [dispatch, userState]);

  const session = React.useMemo(() => {
    return userState
      ? {
          user: {
            id: userState.id || user.id,
            name: userState.firstName || userState.firstName,
            email: userState.email || userState.email,
            image:
              userState.profilePic ||
              "https://avatars.githubusercontent.com/u/19550456",
            role: userState.role || user.role,
          },
        }
      : null;
  }, [userState, user]);

  const authentication = React.useMemo(
    () => ({
      signOut: () => {
        dispatch(logout()); // from authSlice
        dispatch(clearUserData()); // from userSlice
      },
    }),
    [dispatch]
  );

  const router = useDemoRouter("/dashboard");

  const navigationItems = getNavigationForRole(userState?.role || user?.role);

  function customAppBar() {
    return (
      <>
        <Box
          component="img"
          src={logo}
          alt="Logo"
          sx={{
            display: { xs: "flex", sm: "flex", md: "flex" },
            mr: 1,
            height: { xs: 15, sm: 35, md: 35 }, // Set height or width as needed
            borderRadius: 10,
            cursor: "pointer",
          }}
        />
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            textAlign: "center",
            // py: { xs: 2, md: 4 },
          }}
        >
          {/* <Typography
            // variant="h5"
            // component="h4"
            sx={{
              fontWeight: 900,
              letterSpacing: { xs: ".15rem", md: ".65rem" },
              // color: "inherit",
              mb: -1,
              // fontSize: { xs: "2rem", sm: "3rem", md: "3.5rem" },
              textShadow: "2px 2px 4px rgba(0,0,0,0.2)",
            }}
          >
            AASTHA
          </Typography>

          <Typography
            // variant="h6"
            // component="h5"
            sx={{
              // fontWeight: 700,
              // color: "#222",
              // fontSize: { xs: "1.5rem", sm: "2rem", md: "2.5rem" },
              letterSpacing: ".2rem",
            }}
          >
            COMPUTERS
          </Typography> */}
        </Box>
      </>
    );
  }

  return (
    <>
      <SEO
        title="Dashboard - Aastha Computers"
        description="Access your personalized dashboard at Aastha Computers. Whether you're an Admin managing courses and users, a Student tracking your progress and fees, or a Staff member handling schedules and expertise—everything you need is organized in one place."
        pagePath="/admin-dashboard"
      />
      <AppProvider
        session={session}
        authentication={authentication}
        navigation={navigationItems}
        theme={demoTheme}
        router={router}
      >
        <DashboardLayout
          // slots={{
          //   appTitle: customAppBar,
          // }}
          branding={{
            title: "Aastha Computers",
            homeUrl: "/home",
            logo: <img src={logo} alt="Logo" style={{ height: 28 }} />,
          }}
          // navigation={...}
          sx={{
            padding: 2,
          }}
        >
          {!userState ? (
            <Loader fullPage />
          ) : (
            <PageContent
              pathname={router.pathname}
              role={userState?.role}
              userId={userState?.id}
            />
          )}
        </DashboardLayout>
      </AppProvider>
    </>
  );
}

export default AdminDashboard;
